import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@mui/material';
import { FlexContainer } from '@/components/containers/FlexContainer/FlexContainer';
import { useDispatch, useSelector } from 'react-redux';
import { setSearchFormData } from '@/redux/actions/searchFormDataActions';
import IOSTimePicker from '@/components/reusable/IOSTimePicker/IOSTimePicker';
import { useNavigate } from 'react-router-dom';
import './TimeWheelPicker.scss';
import Label from '@/components/form/Label/Label';
import { COWORKING_STRING } from '@/types/spaceTypes.constants';
import { parseTimeString } from '@/helpers/dateHelpers';

const SearchBar = props => {
  const { languageData, onBack } = props;
  return (
    <FlexContainer className={'SearchBar align-items-end'}>
      <i className="fa fa-chevron-left" onClick={onBack}></i>
      <Label className={'fontWhite text-12 margl10'} onClick={onBack}>
        {languageData === 'EN'
          ? 'Back to date picker'
          : 'Volver a seleccionar la fecha'}
      </Label>
    </FlexContainer>
  );
};

const TimeSwitch = props => {
  const { startTime, endTime, isStartActive, onToggle, languageData } = props;
  return (
    <FlexContainer
      className="pos-rel borderrad26  height-45px pointer bgWhite"
      style={{
        border: '1px solid rgba(255,255,255,0.3)',
        minWidth: '250px',
      }}
      onClick={onToggle}
    >
      <FlexContainer
        className="pos-abs top4 bgPink2 borderrad20"
        style={{
          left: isStartActive ? '4px' : 'calc(50% + 2px)',
          width: 'calc(50% - 6px)',
          height: 'calc(100% - 8px)',
          transition: 'left 0.3s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        }}
      />

      <FlexContainer
        className="pos-abs align-items-center justify-center left0 top0 width50 height100 text-14"
        style={{
          color: isStartActive ? 'white' : 'black',
          fontWeight: 'normal',
          transition: 'color 0.3s ease',
          zIndex: 1,
        }}
      >
        {languageData === 'EN' ? 'Start ' : 'Inicio '}{' '}
        <span className="margl6">{startTime}</span>
      </FlexContainer>

      <FlexContainer
        className="pos-abs align-items-center justify-center right0 top0 width50 height100 text-14"
        style={{
          color: isStartActive ? 'black' : 'white',
          fontWeight: 'normal',
          transition: 'color 0.3s ease',
          zIndex: 1,
        }}
      >
        {languageData === 'EN' ? 'End ' : 'Fin '}{' '}
        <span className="margl6">{endTime}</span>
      </FlexContainer>
    </FlexContainer>
  );
};

const convertToComparableTime = time => {
  if (!time || typeof time.hour !== 'number' || !time.period) {
    return 0;
  }
  let hour = time.hour;
  if (time.period === 'PM' && time.hour !== 12) {
    hour += 12;
  }
  if (time.period === 'AM' && time.hour === 12) {
    hour = 0;
  }
  return hour;
};

const ReservationTimeView = props => {
  const { onSubmit, loading, languageData, goToDatePicker, idSubSpace } = props;
  const currentSearchFormData = useSelector(state => state.searchFormData);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isStartActive, setIsStartActive] = useState(true);
  const [pickerKey, setPickerKey] = useState(1);
  const [startTime, setStartTime] = useState({
    hour: 9,
    minute: 0,
    period: 'AM',
    display: '',
  });
  const [endTime, setEndTime] = useState({
    hour: 10,
    minute: 0,
    period: 'PM',
    display: '',
  });

  const [isButtonActiveDisabled, setIsButtonActiveDisabled] = useState(true);
  const spaceType = useSelector(state => state.searchFormData?.spaceType);
  const startimeH = useSelector(state => state.searchFormData?.startTime);
  const endTimeH = useSelector(state => state.searchFormData?.endTime);

  useEffect(() => {
    if (startimeH && endTimeH) {
      console.log('A');
      const parsedStart = parseTimeString(startimeH);
      const parsedEnd = parseTimeString(endTimeH);

      if (parsedStart && parsedEnd) {
        setStartTime(parsedStart);
        setEndTime(parsedEnd);
        return;
      }
    }

    if (spaceType === COWORKING_STRING) {
      console.log('B');
      setStartTime({
        hour: 8,
        minute: 0,
        period: 'AM',
        display: '8:00am',
      });
      setEndTime({
        hour: 7,
        minute: 0,
        period: 'PM',
        display: '7:00pm',
      });
    } else {
      console.log('C');
      setStartTime({
        hour: 8,
        minute: 0,
        period: 'AM',
        display: '8:00am',
      });
      setEndTime({
        hour: 9,
        minute: 0,
        period: 'AM',
        display: '9:00am',
      });
    }
  }, [spaceType, startimeH, endTimeH]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleTimeChange = useCallback(
    value => {
      const hour = value.hour || 9;
      const minute = value.minute || '00';
      const period = value.period || 'AM';
      const display = `${hour}:${minute}${period.toLowerCase()}`;
      const newStartTime = isStartActive ? { hour, minute, period } : startTime;
      const newEndTime = isStartActive ? endTime : { hour, minute, period };
      const startValid =
        convertToComparableTime(newStartTime) >= 7 &&
        convertToComparableTime(newStartTime) <= 23;

      const endValid =
        convertToComparableTime(newEndTime) >= 7 &&
        convertToComparableTime(newEndTime) <= 23;

      setIsButtonActiveDisabled(startValid && endValid);

      if (isStartActive) {
        setStartTime({
          hour,
          minute,
          period,
          display,
        });
      } else {
        setEndTime({
          hour,
          minute,
          period,
          display,
        });
      }
    },
    [isStartActive, startTime, endTime],
  );

  const handleToggle = () => {
    setIsStartActive(!isStartActive);
    setPickerKey(pickerKey + 1);
  };

  const getBackgroundClass = () =>
    isStartActive ? 'bgGradientSignUpPurple180' : 'bgGrandientSkyBlue';

  const formatTimeDisplay = timeObj => {
    if (!timeObj.hour || !timeObj.period) return '--:--';
    const minute = String(timeObj.minute).padStart(2, '0');
    return `${timeObj.hour}:${minute} ${timeObj.period.toLowerCase()}`;
  };

  const formatTimeRedux = timeObj => {
    if (!timeObj.hour || !timeObj.period) return '--:--';
    const minute = String(timeObj.minute).padStart(2, '0');
    return `${timeObj.hour}:${minute}`;
  };

  const handleSubmit = () => {
    const startHour = convertToComparableTime(startTime);
    const endHour = convertToComparableTime(endTime);

    if (endHour > startHour) {
      const data = {
        startTime: formatTimeDisplay(startTime),
        endTime: formatTimeDisplay(endTime),
      };

      onSubmit?.(data);

      dispatch(
        setSearchFormData({
          ...currentSearchFormData,
          startTime: formatTimeDisplay(startTime),
          endTime: formatTimeDisplay(endTime),
        }),
      );

      setIsStartActive(true);

      const isDesktop = document.querySelector('.m-hide') !== null;

      navigate(
        isDesktop
          ? idSubSpace != null && idSubSpace !== ''
            ? '/detalle-espacio/space/' + idSubSpace + '/true'
            : '/'
          : idSubSpace
            ? '/confirmar-reservacion2nd/booking/' + idSubSpace + '/true'
            : '/',
      );
    } else {
      console.log(
        'Error: La hora de fin debe ser posterior a la hora de inicio.',
      );
    }
  };

  const getInitialTimeForPicker = () => (isStartActive ? startTime : endTime);

  const initialTime = getInitialTimeForPicker();

  const allTimesSet =
    startTime.hour && startTime.period && endTime.hour && endTime.period;
  const startHourComparable = convertToComparableTime(startTime);
  const endHourComparable = convertToComparableTime(endTime);
  const isTimeLogicValid = endHourComparable > startHourComparable;

  const isButtonActive =
    allTimesSet && isTimeLogicValid && isButtonActiveDisabled;

  return (
    <FlexContainer
      className={`reservationTimeView flex-column padlr10 justify-start items-center transition-background fontWhite ${getBackgroundClass()}`}
    >
      <SearchBar languageData={languageData} onBack={goToDatePicker} />

      <FlexContainer className={'flex-column padlr20 margb30 height-800px'}>
        <Label className={'text-20 margt20'}>
          {languageData === 'EN' ? 'Select a time' : 'Seleccione un horario'}
        </Label>
        <Label className={'text-14 margtb10'}>
          {languageData === 'EN'
            ? 'Choose the time you would like to book your conference room.'
            : 'Elige el horario en que te gustaría reservar tu sala de conferencias.'}
        </Label>

        <FlexContainer className="justify-center margt20">
          <Label className={'text-20 margb10'}>
            {languageData === 'EN' ? 'Set Time' : 'Seleccionar Horario'}
          </Label>
        </FlexContainer>

        <FlexContainer className="justify-center margb20">
          <TimeSwitch
            startTime={formatTimeDisplay(startTime)}
            endTime={formatTimeDisplay(endTime)}
            isStartActive={isStartActive}
            onToggle={handleToggle}
            languageData={languageData}
          />
        </FlexContainer>

        <IOSTimePicker
          key={pickerKey}
          initialHour={initialTime.hour}
          initialMinute={initialTime.minute}
          initialPeriod={initialTime.period}
          onChange={handleTimeChange}
          minTime={isStartActive ? null : startTime}
        />

        <FlexContainer className={'flex-column margt40 padlr60 padtb10'}>
          <Button
            sx={{
              py: 0.4,
              px: 0.6,
              textTransform: 'none',
              fontSize: '20px',
              fontWeight: 'normal',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '20px',
              '&:disabled': {
                opacity: 0.7,
                color: 'white',
              },
              backgroundColor: isButtonActive
                ? 'white'
                : 'rgba(255, 255, 255, 0.1)',
              color: isButtonActive ? '#E91E63' : 'white',
              '&:hover': {
                backgroundColor: isButtonActive
                  ? 'rgba(255, 255, 255, 0.9)'
                  : 'rgba(255, 255, 255, 0.2)',
              },
            }}
            type="button"
            onClick={handleSubmit}
            disabled={!isButtonActive}
          >
            {loading
              ? languageData === 'EN'
                ? 'Loading...'
                : 'Cargando...'
              : languageData === 'EN'
                ? 'Apply'
                : 'Seleccionar'}
          </Button>
        </FlexContainer>
      </FlexContainer>
    </FlexContainer>
  );
};

export default ReservationTimeView;
